import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Typography,
} from "@material-ui/core";
import ContentCopyIcon from "@material-ui/icons/ContentCopy";
import toast from "react-hot-toast";
import { ValidationException } from "../../customExceptions";
import { setupTwoFactor } from "../../apis/authApi";
import TwoFactorVerify from "./TwoFactorVerify";

const TwoFactorSetup = ({ challengeToken, onVerified }) => {
  // States

  const [setup, setSetup] = useState(null);
  const [setupError, setSetupError] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);

  // Called directly rather than through useMutation: StrictMode's simulated unmount
  // detaches the mutation observer, and a second call would replace the pending
  // secret shown in the QR code.
  const hasRequestedSetup = useRef(false);
  useEffect(() => {
    if (hasRequestedSetup.current) return;
    hasRequestedSetup.current = true;
    setupTwoFactor({ challengeToken })
      .then(setSetup)
      .catch((error) => {
        setSetupError(
          error instanceof ValidationException
            ? error.validationErrors.submit || error.message
            : "Two-factor setup failed, please try again later"
        );
      });
  }, [challengeToken]);

  const copyRecoveryCodes = () => {
    navigator.clipboard
      .writeText(verifyResult.recoveryCodes.join("\n"))
      .then(() => toast.success("Recovery codes copied"))
      .catch(() => toast.error("Copy failed, please select the codes manually"));
  };

  // Rendering

  if (verifyResult) {
    return (
      <Box>
        <Typography color="textPrimary" variant="h6" gutterBottom>
          Save your recovery codes
        </Typography>
        <Typography color="textSecondary" variant="body2" gutterBottom>
          If you lose access to your authenticator app, each of these codes
          lets you log in once. Store them somewhere safe: they will not be
          shown again.
        </Typography>
        <Box
          component="pre"
          sx={{
            backgroundColor: "background.default",
            borderRadius: 1,
            fontFamily: "monospace",
            my: 2,
            p: 2,
          }}
        >
          {verifyResult.recoveryCodes.join("\n")}
        </Box>
        <Button
          fullWidth
          size="large"
          variant="outlined"
          startIcon={<ContentCopyIcon />}
          onClick={copyRecoveryCodes}
          sx={{ mb: 2 }}
        >
          Copy codes
        </Button>
        <Button
          color="primary"
          fullWidth
          size="large"
          variant="contained"
          onClick={() => onVerified(verifyResult)}
        >
          I have saved my recovery codes
        </Button>
      </Box>
    );
  }

  if (setupError) {
    return <FormHelperText error>{setupError}</FormHelperText>;
  }

  if (!setup) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography color="textPrimary" variant="h6" gutterBottom>
        Set up two-factor authentication
      </Typography>
      <Typography color="textSecondary" variant="body2">
        Scan this QR code with an authenticator app (Google Authenticator,
        1Password, Authy...), then enter the 6-digit code it shows.
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
        <img src={setup.qrCodeDataUrl} alt="2FA QR code" width={200} />
      </Box>
      <Typography color="textSecondary" variant="body2" align="center">
        Can't scan it? Enter this key manually:
      </Typography>
      <Typography
        color="textPrimary"
        variant="body2"
        align="center"
        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
      >
        {setup.secret}
      </Typography>
      <TwoFactorVerify
        challengeToken={challengeToken}
        onVerified={setVerifyResult}
      />
    </Box>
  );
};

export default TwoFactorSetup;
